import { useRef } from "react";
import RDatePicker from "react-datepicker";
import { isBefore, isValid, parse } from "date-fns";
import { useParams } from "react-router-dom";
import { ru, enUS } from "date-fns/locale";
import { IconSvg } from "../../../utils/constants/icons/iconSvg.constant";
import { CustomInput } from "../../ui/CustomInput/CustomInput";
import { useTranslation } from "../../../hooks";
import { capitalize } from "../../../utils/helpers";
import "./DatePicker.scss";

export const DatePicker = ({ startDate, setStartDate, endDate, setEndDate }) => {
  const { lang } = useParams();
  const endDateRef = useRef(null);

  const { cabinet: { orders: { filter: { from, to } } } } = useTranslation();

  const locales = {
    ru,
    en: enUS
  };

  const currentLocale = locales[lang] || enUS;

  const renderHeader = ({ date, decreaseMonth, increaseMonth, prevMonthButtonDisabled, nextMonthButtonDisabled }) => (
    <div className="react-datepicker__custom-header">
      <button className="react-datepicker__arrow react-datepicker__arrow--left" onClick={decreaseMonth} disabled={prevMonthButtonDisabled}>
        <IconSvg tag="arrowRight" />
      </button>
      <span>{capitalize(date.toLocaleString(lang, { month: "long" }))} {date.getFullYear()}</span>
      <button className="react-datepicker__arrow react-datepicker__arrow--right" onClick={increaseMonth} disabled={nextMonthButtonDisabled}>
        <IconSvg tag="arrowRight" />
      </button>
    </div>
  );

  const commonProps = {
    dateFormat: "dd.MM.yyyy",
    isClearable: false,
    renderCustomHeader: renderHeader,
    locale: currentLocale
  };

  const handleEndDateChange = (date) => {
    if (startDate && date && isBefore(date, startDate)) {
      setStartDate(date);
      setEndDate(null);
      return;
    }

    setEndDate(date);
  };

  const handleStartDateChange = (date) => {
    setStartDate(date);

    if (endDate && date && isBefore(endDate, date)) {
      setEndDate(null);
    }

    if (endDateRef.current) {
      endDateRef.current.setOpen();
    }
  };

  const clearStartDateField = () => {
    setStartDate(null);
  };

  const clearEndDateField = () => {
    setEndDate(null);
  };

  const dateStartInputHandler = (e) => {
    if (!e.target.value) return;

    const parsed = parse(e.target.value, "dd.MM.yyyy", new Date());
    if (isValid(parsed)) handleStartDateChange(parsed);
  };

  const dateEndInputHandler = (e) => {
    if (!e.target.value) return;

    const isFullYear = /^\d{2}\.\d{2}\.\d{4}$/.test(e.target.value);
    if (!isFullYear) return;

    const parsed = parse(e.target.value, "dd.MM.yyyy", new Date());
    if (isValid(parsed)) handleEndDateChange(parsed);
  };

  return (
    <div className="date-picker-container">
      <div className="date-picker__selects">
        <div className="date-picker__select-wrapper">
          <RDatePicker
            customInput={<CustomInput
                testId="dateFrom"
                label={from}
                icon={startDate ? "xIcon" : "datePicker"}
                iconHandler={startDate && clearStartDateField}
              />
            }
            selected={startDate}
            onChange={handleStartDateChange}
            selectsStart
            startDate={startDate}
            endDate={endDate}
            onChangeRaw={dateStartInputHandler}
            {...commonProps}
          />
        </div>
        <div className="date-picker__select-wrapper">
          <RDatePicker
            ref={endDateRef}
            customInput={<CustomInput
              testId="dateTo"
              label={to}
              icon={endDate ? "xIcon" : "datePicker"}
              iconHandler={endDate && clearEndDateField}
            />
            }
            selected={endDate}
            onChange={handleEndDateChange}
            selectsEnd
            startDate={startDate}
            endDate={endDate}
            minDate={startDate}
            onChangeRaw={dateEndInputHandler}
            {...commonProps}
          />
        </div>
        </div>
      </div>
      );
      };
